import React from 'react';
import { CheckCircle2, XCircle, Pencil, Send, Trash2 } from 'lucide-react';
import WorkflowStatusBadge from './WorkflowStatusBadge';
import { LockTip } from '../shared/FormKit';
import { commentLabel, useFieldCommentsContext } from './fieldComments';
import { formatDateTime } from '../../utils/dateFormat';
import { actorLabel, decisionLine, latestEventOf } from './eventActor';
import {
  btnApprove, btnReject, btnResubmit, btnOutline, btnDeleteOutline,
  calloutReturned, calloutReturnedTitle, calloutReturnedBody, calloutReturnedMeta,
  calloutLocked, calloutLockedTitle,
  detailLabel,
} from './constants';

// The one place a request's state meets its actions (docs/workflow-ui.md §4.3).
// Top: one status block per side (single-status flows pass one unlabelled
// side; Engagement Setup passes client/eng/cw/cc). Middle: the reviewer's
// return reason while the request is awaiting_info, then `beforeActions` —
// whatever the reviewer must supply before Approve will go through.
// Bottom: reviewer actions
// (Approve per pending side — green; one Return for Changes — red) then
// applicant actions
// (Modify / Re-submit / Delete). Callers pass null handlers to hide a button;
// a flow whose Modify opens a full edit page passes `modifyLabel="Modify &
// Re-submit"` and no `onResubmit` — re-submitting is the edit page's own
// submit button, so the applicant row is Modify & Re-submit + Delete;
// approval side-effects (charge-code popup, archival-deadline modal) live in
// the caller's onApprove.
//
// sides: [{ key, label|null, status, note?, approveDisabled?, disabledReason? }]
//
// showDecider: key-reviewed flows (anyone holding the key may decide) pass it,
// so an approved / completed side reads "Approved by <name> · <time>" from the
// History feed instead of a bare date. Chosen-reviewer flows leave it off —
// their ApprovalsCard already names who signed each stage.
//
// lock: the detail endpoint's `engagementLock` — { locked, engagements:
// [{ engagementName, status }] }. When locked (a covered engagement is
// Completed / Discontinued — docs/workflow-contract.md §2d)
// Approve / Modify / Re-submit are hidden and an amber note carries just the
// title "Engagement closed — workflow locked"; Return for changes and Delete
// stay, so a stranded request can still be cleared.

/**
 * The reason a request was last sent back, or null.
 *
 * Every flow logs the same `rejected` event with the reviewer's reason
 * (server/routes/workflowReject.js → repo.reject), and every events feed comes
 * back newest-first, so the first match is the current one. Nothing is stored
 * on the request row — this reads what the pages already load for History.
 * Exported so a page can reuse it (e.g. a list-row tooltip) without copying
 * the shape assumption.
 */
export const latestRejection = (events = []) =>
  (events || []).find((ev) => ev.action === 'rejected' && ev.reason) || null;

// The event that settled a side, by the side's status (showDecider).
const DECISION = {
  approved: { verb: 'Approved', actions: ['approved'] },
  completed: { verb: 'Completed', actions: ['completed'] },
};

const StatusActionsCard = ({
  sides,
  busy = false,
  events = [],               // the History feed — used to surface the return reason
  onApprove = null,          // (sideKey) => void — hidden when null
  approveLabel = null,       // (side) => string; default 'Approve' / 'Approve <label>'
  onReject = null,           // () => void (page opens RejectModal)
  rejectLabel = 'Return for Changes',
  onModify = null,
  modifyLabel = 'Modify',    // 'Modify & Re-submit' where Modify opens the edit page
  onResubmit = null,         // page shows only when status is awaiting_info
  onDelete = null,
  beforeActions = null,      // node — what the reviewer must supply to approve
  lock = null,               // engagement closed → see the header comment
  showDecider = false,       // key-reviewed flows → see the header comment
  statusMeta = undefined,    // a flow's own status wording (CPD_STATUS_META)
  children,                  // extra status rows (reason, master links…)
}) => {
  // 結案鎖定：三個「推進」動作一起藏，退件與刪除照留（契約 §2d）。
  const locked = !!(lock && lock.locked);
  const approveFn = locked ? null : onApprove;
  const modifyFn = locked ? null : onModify;
  const resubmitFn = locked ? null : onResubmit;
  // 意見的欄位名：頁面若提供了對照表就用它救回舊資料寫成裸 key 的那些
  // （commentLabel 的規則），沒有 provider 時就照存下來的顯示。
  const fc = useFieldCommentsContext();
  const commentLabelFor = fc ? fc.labelFor : ((f) => f);
  const pending = sides.filter((s) => s.status === 'requested');
  // Only while the request is actually sitting in awaiting_info: once it is
  // re-submitted or approved the reason is history, and History is where it
  // belongs. CWCC rejects every pending side at once, so "some side" is right.
  const returned = sides.some((s) => s.status === 'awaiting_info')
    ? latestRejection(events)
    : null;
  // A returned side's `note` is its status_updated_at — the same instant the
  // callout below already stamps. Show it once, on the line that says who.
  const noteOf = (s) => {
    if (returned && s.status === 'awaiting_info') return null;
    const decision = showDecider && DECISION[s.status];
    return (decision && decisionLine(decision.verb, latestEventOf(events, decision.actions))) || s.note;
  };
  const labelFor = (side) =>
    (approveLabel ? approveLabel(side) : (side.label ? `Approve ${side.label}` : 'Approve'));
  const hasReviewerRow = (approveFn && pending.length > 0) || (onReject && pending.length > 0);
  const hasApplicantRow = modifyFn || resubmitFn || onDelete;

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-4 mb-4">
      <div className="flex flex-wrap gap-8">
        {sides.map((s) => (
          <div key={s.key}>
            <p className={detailLabel}>{s.label || 'Review Status'}</p>
            <div className="mt-1"><WorkflowStatusBadge status={s.status} meta={statusMeta} /></div>
            {noteOf(s) && <p className="text-xs text-slate-500 mt-1">{noteOf(s)}</p>}
          </div>
        ))}
        {children}
      </div>

      {returned && (
        <div className={calloutReturned}>
          <div className="min-w-0">
            <p className={calloutReturnedTitle}>Reviewer&rsquo;s reason</p>
            <p className={calloutReturnedBody}>{returned.reason}</p>
            {/* 逐項意見：整體原因說「為什麼退」，這裡說「哪一格」。
                同一顆事件，所以永遠一起出現、一起消失。每一格旁邊也有一份
                （DetailItem），這裡是那些散落便條的總表——申請者不必為了
                找出還有哪幾項沒看到而把整頁捲一遍。 */}
            {(returned.fieldComments || []).length > 0 && (
              <ul className="mt-2 space-y-1 border-t border-red-200 pt-2">
                {returned.fieldComments.map((c) => (
                  <li key={c.field} className="text-sm text-red-900 flex flex-wrap gap-x-1.5">
                    <span className="font-medium">{commentLabel(c, commentLabelFor)}:</span>
                    <span className="whitespace-pre-wrap break-words">{c.comment}</span>
                  </li>
                ))}
              </ul>
            )}
            <p className={calloutReturnedMeta}>
              Returned by {actorLabel(returned)} · {formatDateTime(returned.createdAt)}
            </p>
          </div>
        </div>
      )}

      {locked && (
        <div className={calloutLocked}>
          <p className={calloutLockedTitle}>Engagement closed — workflow locked</p>
        </div>
      )}

      {/* The reviewer's own prerequisite, directly above the button it unlocks —
          a disabled Approve with the explanation two screens down is a dead
          end. */}
      {beforeActions}

      {(hasReviewerRow || hasApplicantRow) && (
        <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-slate-100">
          {approveFn && pending.map((s) => (
            <LockTip key={s.key} text={s.approveDisabled ? s.disabledReason : null}>
              <button
                type="button"
                onClick={() => approveFn(s.key)}
                disabled={busy || s.approveDisabled}
                className={btnApprove}
              >
                <CheckCircle2 className="w-4 h-4" /> {labelFor(s)}
              </button>
            </LockTip>
          ))}
          {onReject && pending.length > 0 && (
            <button type="button" onClick={onReject} disabled={busy} className={btnReject}>
              <XCircle className="w-4 h-4" /> {rejectLabel}
            </button>
          )}
          {hasReviewerRow && hasApplicantRow && <span className="w-px h-6 bg-slate-200 mx-1" />}
          {modifyFn && (
            <button type="button" onClick={modifyFn} disabled={busy} className={btnOutline}>
              <Pencil className="w-4 h-4" /> {modifyLabel}
            </button>
          )}
          {resubmitFn && (
            <button
              type="button"
              onClick={resubmitFn}
              disabled={busy}
              title="Send this request back for review — with or without changes"
              className={btnResubmit}
            >
              <Send className="w-4 h-4" /> Re-submit
            </button>
          )}
          {onDelete && (
            <button type="button" onClick={onDelete} disabled={busy} className={btnDeleteOutline}>
              <Trash2 className="w-4 h-4" /> Delete
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default StatusActionsCard;
